import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, USER_STATUS } from "@/contexts/AuthContext";
import { AppLogo } from "@/components/AppLogo";

export const BlockedPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, hasAccess, logout } = useAuth();

  // Redirect if user has access or is not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (hasAccess) {
      navigate("/");
    }
  }, [isAuthenticated, hasAccess, navigate]);

  const handleLogout = () => {
    navigate("/login");
    setTimeout(() => logout(), 0);
  };

  const getStatusContent = () => {
    if (!user) return null;

    switch (user.status) {
      case USER_STATUS.EXPIRADO:
        return {
          icon: <Clock className="w-16 h-16 text-amber-500" />,
          title: "Seu período de teste terminou",
          description: "Seu teste gratuito de 7 dias expirou. Assine agora para continuar usando o Audio Ideias e manter acesso às suas ideias salvas.",
          actionText: "Escolher plano",
          actionLink: "/pagamento",
        };
      case USER_STATUS.AGUARDANDO_APROVACAO:
        return {
          icon: <Clock className="w-16 h-16 text-blue-500" />,
          title: "Aguardando aprovação",
          description: "Seu pagamento está sendo processado. Assim que for aprovado, você terá acesso completo à plataforma.",
          actionText: "Verificar status",
          actionLink: "/pagamento",
        };
      case USER_STATUS.BLOQUEADO:
        return {
          icon: <AlertTriangle className="w-16 h-16 text-rose-500" />,
          title: "Acesso bloqueado",
          description: "Seu acesso foi bloqueado. Entre em contato com o suporte para mais informações.",
          actionText: "Contatar suporte",
          actionLink: "mailto:suporte@voiceideias.com",
        };
      default:
        return {
          icon: <AlertTriangle className="w-16 h-16 text-rose-500" />,
          title: "Acesso não autorizado",
          description: "Você não tem permissão para acessar esta área.",
          actionText: "Voltar ao início",
          actionLink: "/",
        };
    }
  };

  const content = getStatusContent();

  if (!content) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: 'var(--vi-page)' }} data-testid="blocked-page">
      {/* Logo */}
      <div className="mb-8 animate-fade-in-up">
        <AppLogo size="lg" />
      </div>

      {/* Content Card */}
      <div className="w-full max-w-md animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
        <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }}>
          <div className="flex justify-center mb-6 animate-scale-in" style={{ animationDelay: "0.2s" }}>
            {content.icon}
          </div>

          <h2 className="font-heading text-xl md:text-2xl font-bold mb-4" style={{ color: 'var(--vi-text)' }}>
            {content.title}
          </h2>

          <p className="mb-8" style={{ color: 'var(--vi-text-muted)' }}>
            {content.description}
          </p>

          {user?.status === USER_STATUS.EXPIRADO && (
            <div className="rounded-xl p-4 mb-6 text-left" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
              <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--vi-text)' }}>Suas ideias estão seguras!</h3>
              <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                Todas as suas ideias salvas continuam disponíveis. Após assinar, você terá acesso completo novamente.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {content.actionLink.startsWith("mailto:") ? (
              <a href={content.actionLink} className="block">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11">
                  {content.actionText}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            ) : (
              <Link to={content.actionLink}>
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11"
                  data-testid="action-button"
                >
                  {content.actionText}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full border"
              style={{ borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text-muted)' }}
              data-testid="logout-button"
            >
              Sair da conta
            </Button>
          </div>
        </div>

        {/* User info */}
        {user && (
          <div className="mt-4 text-center animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <p className="text-xs" style={{ color: 'var(--vi-text-muted)' }}>
              Logado como <span style={{ color: 'var(--vi-text-soft)' }}>{user.email}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedPage;
